import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  VStack,
  HStack,
  Select,
  Switch,
  FormHelperText,
  Grid,
  Spinner,
  Text,
  Divider,
} from '@chakra-ui/react'
import { storeFormSchema, type StoreFormData } from '@/lib/validations/store'
import {
  createStore,
  updateStore,
  fetchStore,
  fetchBusinessUnitOptions,
  fetchRegionOptions,
  fetchProvinceOptions,
} from '@/lib/services/storeService'
import { fetchClientOptionsList } from '@/lib/services/clientContextService'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useClientContextStore } from '@/store/clientContext'

interface StoreFormProps {
  storeId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

export function StoreForm({ storeId, onSuccess, onCancel }: StoreFormProps) {
  const colors = useThemeColors()
  const activeClientCode = useClientContextStore((s) => s.activeClientCode)
  const setActiveClient = useClientContextStore((s) => s.setActiveClient)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [clientOptions, setClientOptions] = useState<Array<{ id: number; code: string; name: string; label: string; database_name?: string }>>([])
  const [businessUnitOptions, setBusinessUnitOptions] = useState<Array<{ id: number; label: string }>>([])
  const [selectedClientCode, setSelectedClientCode] = useState<string | null>(activeClientCode ?? null)
  const [regions, setRegions] = useState<Array<{ id: number; name: string }>>([])
  const [provinces, setProvinces] = useState<Array<{ id: number; name: string }>>([])
  const { showError, showSuccess } = useToastNotification()
  const isEditMode = !!storeId

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
    clearErrors,
    watch,
    reset,
    getValues,
  } = useForm<StoreFormData>({
    resolver: zodResolver(storeFormSchema),
    defaultValues: {
      branch_code: '',
      branch_name: '',
      branch_address: '',
      region_id: null,
      province_id: null,
      location_address: '',
      normalized_address: '',
      latitude: undefined,
      longitude: undefined,
      branch_email: '',
      contact_number: '',
      mobile_number: '',
      contact_person: '',
      active: true,
    },
  })

  const activeValue = watch('active')
  const regionId = watch('region_id')

  useEffect(() => {
    if (activeClientCode) {
      setSelectedClientCode(activeClientCode)
    }
  }, [activeClientCode])

  useEffect(() => {
    const loadClients = async () => {
      try {
        const clients = await fetchClientOptionsList()
        setClientOptions(
          clients.map((client) => ({
            id: client.id,
            code: client.code,
            name: client.name,
            database_name: client.database_name,
            label: `${client.name} (${client.code})`,
          }))
        )
      } catch (error: unknown) {
        showError(error)
      }
    }
    loadClients()
  }, [showError])

  useEffect(() => {
    if (!selectedClientCode) {
      setBusinessUnitOptions([])
      setRegions([])
      setProvinces([])
      setValue('business_unit_id', undefined)
      setValue('region_id', null)
      setValue('province_id', null)
      return
    }

    const loadClientScopedOptions = async () => {
      try {
        const [businessUnits, regionItems] = await Promise.all([
          fetchBusinessUnitOptions(selectedClientCode),
          fetchRegionOptions(selectedClientCode),
        ])
        setBusinessUnitOptions(businessUnits)
        setRegions(regionItems)
        const currentBusinessUnitId = getValues('business_unit_id')
        const hasCurrentBusinessUnit = businessUnits.some((bu) => bu.id === currentBusinessUnitId)
        if (!hasCurrentBusinessUnit) {
          setValue('business_unit_id', undefined)
        }
        clearErrors('business_unit_id')
      } catch {
        setBusinessUnitOptions([])
        setRegions([])
        setValue('business_unit_id', undefined)
      }
    }
    loadClientScopedOptions()
  }, [selectedClientCode, setValue, getValues, clearErrors])

  useEffect(() => {
    if (!regionId || !selectedClientCode) {
      setProvinces([])
      return
    }

    const loadProvinces = async () => {
      try {
        const items = await fetchProvinceOptions(Number(regionId), selectedClientCode)
        setProvinces(items)
      } catch (error: unknown) {
        setProvinces([])
      }
    }
    loadProvinces()
  }, [regionId, selectedClientCode])

  useEffect(() => {
    if (!isEditMode || !storeId || !activeClientCode) return

    const loadStore = async () => {
      setIsLoading(true)
      try {
        const store = await fetchStore(storeId)
        reset({
          business_unit_id: store.business_unit_id,
          branch_code: store.branch_code ?? '',
          branch_name: store.branch_name ?? '',
          branch_address: store.branch_address ?? '',
          region_id: store.region_id ?? null,
          province_id: store.province_id ?? null,
          location_address: store.location_address ?? '',
          normalized_address: store.normalized_address ?? '',
          latitude: store.latitude ?? undefined,
          longitude: store.longitude ?? undefined,
          branch_email: store.branch_email ?? '',
          contact_number: store.contact_number ?? '',
          mobile_number: store.mobile_number ?? '',
          contact_person: store.contact_person ?? '',
          active: store.active === 'Inactive' ? false : store.active !== false,
        })
      } catch (error: unknown) {
        showError(error)
      } finally {
        setIsLoading(false)
      }
    }
    loadStore()
  }, [isEditMode, storeId, activeClientCode, reset, showError])

  const onSubmit = async (values: StoreFormData) => {
    const selectedClient = clientOptions.find((c) => c.code === selectedClientCode)
    if (!selectedClient) {
      showError(new Error('Please select a Business Unit (Client) before saving'))
      return
    }
    if (businessUnitOptions.length > 0 && !values.business_unit_id) {
      setError('business_unit_id', {
        type: 'manual',
        message: 'Please select a business unit',
      })
      return
    }

    setActiveClient({
      id: selectedClient.id,
      code: selectedClient.code,
      name: selectedClient.name,
      database_name: selectedClient.database_name,
    })

    setIsSubmitting(true)
    try {
      if (isEditMode && storeId) {
        await updateStore(storeId, values, selectedClient.code)
        showSuccess('Success', 'Store updated successfully')
      } else {
        await createStore(values, selectedClient.code)
        showSuccess('Success', 'Store created successfully')
      }
      onSuccess?.()
    } catch (error: unknown) {
      showError(error)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isEditMode && !activeClientCode) {
    return (
      <Text color={colors.text}>Open this page from the Stores list so the Business Unit (Client) context is available.</Text>
    )
  }

  if (isLoading) {
    return (
      <Box py={8} textAlign="center">
        <Spinner />
      </Box>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)} autoComplete="off">
      <VStack spacing={4} align="stretch">
        <Text fontSize="md" fontWeight="semibold">Store Profile</Text>
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Business Unit (Client) <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl>
            <Select
              placeholder="Select client"
              value={selectedClientCode ?? ''}
              onChange={(e) => {
                const code = e.target.value || null
                setSelectedClientCode(code)
                setValue('business_unit_id', undefined)
                setValue('region_id', null)
                setValue('province_id', null)
                setProvinces([])
              }}
            >
              {clientOptions.map((client) => (
                <option key={client.id} value={client.code}>
                  {client.label}
                </option>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {businessUnitOptions.length > 0 ? (
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">
              Business Unit <Text as="span" color="red.500">*</Text>
            </FormLabel>
            <FormControl isInvalid={!!errors.business_unit_id}>
              <Select
                placeholder="Select business unit"
                {...register('business_unit_id', {
                  setValueAs: (v) => (v === '' || v === undefined ? undefined : Number(v)),
                })}
                isDisabled={!selectedClientCode}
              >
                {businessUnitOptions.map((bu) => (
                  <option key={bu.id} value={bu.id}>
                    {bu.label}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.business_unit_id?.message}</FormErrorMessage>
            </FormControl>
          </Grid>
        ) : (
          <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
            <FormLabel pt={2} fontWeight="medium">Business Unit</FormLabel>
            <FormControl>
              <Text fontSize="sm" color={colors.text}>
                No business units configured for this client. Store will be saved without business unit assignment.
              </Text>
            </FormControl>
          </Grid>
        )}

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Branch Code <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.branch_code}>
            <Input {...register('branch_code')} placeholder="e.g., STR001" />
            <FormErrorMessage>{errors.branch_code?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Branch Name <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.branch_name}>
            <Input {...register('branch_name')} placeholder="e.g., SM North EDSA" />
            <FormErrorMessage>{errors.branch_name?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Branch Address</FormLabel>
          <FormControl isInvalid={!!errors.branch_address}>
            <Input {...register('branch_address')} placeholder="Primary store address" />
            <FormErrorMessage>{errors.branch_address?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Region</FormLabel>
          <FormControl>
            <Select
              placeholder="Select region"
              value={regionId ?? ''}
              onChange={(e) => {
                const val = e.target.value ? Number(e.target.value) : null
                setValue('region_id', val)
                setValue('province_id', null)
              }}
            >
              {regions.map((region) => (
                <option key={region.id} value={region.id}>
                  {region.name}
                </option>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Province</FormLabel>
          <FormControl isInvalid={!!errors.province_id}>
            <Select
              placeholder="Select province"
              {...register('province_id', {
                setValueAs: (v) => (v === '' || v === undefined ? undefined : Number(v)),
              })}
              isDisabled={!regionId}
            >
              {provinces.map((province) => (
                <option key={province.id} value={province.id}>
                  {province.name}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.province_id?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Divider />
        <Text fontSize="md" fontWeight="semibold">Contact Details</Text>
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Branch Email <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl isInvalid={!!errors.branch_email}>
            <Input type="email" {...register('branch_email')} placeholder="e.g., branch@company.com" />
            <FormErrorMessage>{errors.branch_email?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Contact Person</FormLabel>
          <FormControl isInvalid={!!errors.contact_person}>
            <Input {...register('contact_person')} placeholder="Optional contact person" />
            <FormErrorMessage>{errors.contact_person?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Contact Number</FormLabel>
          <FormControl isInvalid={!!errors.contact_number}>
            <Input {...register('contact_number')} placeholder="Required if mobile number is empty" />
            <FormErrorMessage>{errors.contact_number?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Mobile Number</FormLabel>
          <FormControl isInvalid={!!errors.mobile_number}>
            <Input {...register('mobile_number')} placeholder="Required if contact number is empty" />
            <FormErrorMessage>{errors.mobile_number?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Divider />
        <Text fontSize="md" fontWeight="semibold">Location Details</Text>
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Location Address</FormLabel>
          <FormControl isInvalid={!!errors.location_address}>
            <Input {...register('location_address')} placeholder="Optional location-specific address" />
            <FormErrorMessage>{errors.location_address?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Normalized Address</FormLabel>
          <FormControl isInvalid={!!errors.normalized_address}>
            <Input {...register('normalized_address')} placeholder="Optional normalized/geocoded address" />
            <FormErrorMessage>{errors.normalized_address?.message}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Coordinates <Text as="span" color="red.500">*</Text>
          </FormLabel>
          <FormControl>
            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={3}>
              <FormControl isInvalid={!!errors.latitude}>
                <Input
                  type="number"
                  step="any"
                  {...register('latitude', { valueAsNumber: true })}
                  placeholder="Latitude (e.g. 14.5995)"
                />
                <FormErrorMessage>{errors.latitude?.message}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!errors.longitude}>
                <Input
                  type="number"
                  step="any"
                  {...register('longitude', { valueAsNumber: true })}
                  placeholder="Longitude (e.g. 120.9842)"
                />
                <FormErrorMessage>{errors.longitude?.message}</FormErrorMessage>
              </FormControl>
            </Grid>
          </FormControl>
        </Grid>

        <Divider />
        <Text fontSize="md" fontWeight="semibold">Status</Text>
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Active</FormLabel>
          <FormControl>
            <HStack>
              <Switch
                isChecked={activeValue}
                onChange={(e) => setValue('active', e.target.checked)}
              />
              <Text fontSize="sm" color={colors.text}>
                {activeValue ? 'Active' : 'Inactive'}
              </Text>
            </HStack>
            <FormHelperText>Inactive stores are hidden from most workflows.</FormHelperText>
          </FormControl>
        </Grid>

        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button variant="outline" onClick={onCancel} isDisabled={isSubmitting} type="button">
              Cancel
            </Button>
          )}
          <Button type="submit" colorScheme="blue" isLoading={isSubmitting} loadingText={isEditMode ? 'Saving...' : 'Creating...'}>
            {isEditMode ? 'Save' : 'Create Store'}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}
